import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import cn from 'classnames'
import { FormattedMessage } from 'react-intl'
import {
  selectPricingConfig,
  getPlanWithLowestMonthlyPrice,
  selectCoupon,
} from 'js/store/pricingSlice'
import useLocale from 'js/hooks/useLocale'

import ChooseSubscriptionProduct from './ChooseSubscriptionProduct'
import BestPlanInfo from './BestPlanInfo'
import { mergePricingConfigs } from 'js/utils/mergePricingConfigs'

function ChooseSubscription({
  onSelectSubscription,
  planToSelect,
  include24Months = false,
}) {
  const pricingConfig = useSelector(selectPricingConfig())
  const regularPricingConfig = useSelector(selectPricingConfig(null))
  const coupon = useSelector(selectCoupon)
  const [selectedProduct, setSelectedProduct] = useState({})
  const { country } = useLocale()

  useEffect(() => {
    if (!pricingConfig) return
    if (pricingConfig.products.length < 1) return
    const preSelectedProduct = pricingConfig.products
      .filter((config) => config.duration !== 24)
      .slice(-1)[0]

    setSelectedProduct(preSelectedProduct)
  }, [pricingConfig])

  const products = mergePricingConfigs(
    pricingConfig,
    regularPricingConfig
  ).filter((product) => {
    if (product.discount === 100) return true
    if (country === 'US' && product.duration === 1) return false
    if (country !== 'US' && product.duration === 6) return false
    if (!include24Months && product.duration === 24) return false
    return true
  })

  if (!regularPricingConfig) return null

  const currency = pricingConfig.currency
  const bestPlan = getPlanWithLowestMonthlyPrice(products)

  if (planToSelect) {
    return onSelectSubscription(planToSelect)
  }

  return (
    <div className="mt-36">
      <div
        className={cn(
          `grid grid-cols-1 lg:grid-cols-${products.length} gap-0 justify-center`,
          'mb-24 text-center mx-auto max-w-1024',
          'shadow-elevated lg:shadow-none rounded-12',
          products.length > 3 ? 'lg:gap-4' : 'lg:gap-6'
        )}
        data-testid="choose-subscription"
      >
        {products.map((product) => {
          return (
            <ChooseSubscriptionProduct
              key={product.duration}
              {...product}
              currency={currency}
              selected={product.duration === selectedProduct.duration}
              onClick={() => {
                setSelectedProduct(product)
              }}
              isFallbackPrice={coupon && !product.discount}
              bestPlanInfo={
                bestPlan.duration === product.duration ? (
                  <BestPlanInfo
                    currency={currency}
                    originalPrice={
                      bestPlan.coupon && bestPlan.discount > 0
                        ? bestPlan.originalPrice / bestPlan.duration
                        : products[0].price
                    }
                    price={bestPlan.price / bestPlan.duration}
                    duration={bestPlan.duration}
                    discountType={product.discountType}
                    isRegularPrice={!bestPlan.discount}
                  />
                ) : null
              }
              trialDuration={product.trialDuration}
              onSelectProduct={() => onSelectSubscription(product)}
            />
          )
        })}
      </div>
      {selectedProduct.duration && (
        <button
          className={`
            block lg:hidden w-full mb-16
            bg-green text-white type-upper-fat
            rounded-full py-16 shadow-elevated
          `}
          onClick={() => onSelectSubscription(selectedProduct)}
          data-testid="choose-button-mobile"
        >
          {selectedProduct.duration === 1 ? (
            <FormattedMessage
              id="chooseSubscription.chooseButton_singular"
              defaultMessage="Choose {duration} month"
              values={{ duration: selectedProduct.duration }}
            />
          ) : (
            <FormattedMessage
              id="chooseSubscription.chooseButton_plural"
              defaultMessage="Choose {duration} months"
              values={{ duration: selectedProduct.duration }}
            />
          )}
        </button>
      )}
    </div>
  )
}

ChooseSubscription.propTypes = {
  onSelectSubscription: PropTypes.func,
  planToSelect: PropTypes.number,
  include24Months: PropTypes.bool,
}

export default ChooseSubscription
